import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Send, Loader2, Plus, Trash2, UserPlus, Edit, Power, Save,
  GripVertical, Type, Image, ChevronUp, ChevronDown
} from 'lucide-react';
import { useAdminEmail } from './AdminEmailContext';

const OnboardingTab = () => {
  const ctx = useAdminEmail();
  const {
    onboardingTemplates, loadingOnboarding,
    openOnboardingModal, toggleOnboardingTemplate, togglingTemplate,
    testOnboardingTemplate, deleteOnboardingTemplate,
    showOnboardingModal, setShowOnboardingModal,
    editingTemplate, onboardingForm, setOnboardingForm,
    saveOnboardingTemplate, savingOnboarding,
    // content blocks
    onboardingBlocks, onboardingDraggedIndex, onboardingUploadingBlock,
    addOnboardingBlock, updateOnboardingBlock, removeOnboardingBlock,
    moveOnboardingBlock,
    handleOnboardingDragStart, handleOnboardingDragOver, handleOnboardingDragEnd,
    handleOnboardingImageUpload,
  } = ctx;

  return (
    <div className="onboarding-tab">
      <Card className="onboarding-card">
        <CardHeader>
          <div className="onboarding-header-row">
            <div>
              <CardTitle>New User Onboarding Emails</CardTitle>
              <CardDescription>Automated emails sent to new users based on signup date</CardDescription>
            </div>
            <Button onClick={() => openOnboardingModal()} className="create-template-btn" data-testid="create-onboarding-template">
              <Plus size={16} />
              New Template
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loadingOnboarding ? (
            <div className="loading-state">
              <Loader2 size={32} className="spinning" />
              <p>Loading templates...</p>
            </div>
          ) : onboardingTemplates.length === 0 ? (
            <div className="empty-state">
              <UserPlus size={48} />
              <p>No onboarding templates yet</p>
              <p className="empty-hint">Create templates to automatically welcome and guide new users.</p>
            </div>
          ) : (
            <div className="onboarding-templates-list">
              {onboardingTemplates.map((template, idx) => (
                <div
                  key={template.id}
                  className={`onboarding-template-item ${template.enabled ? '' : 'disabled'}`}
                  data-testid={`onboarding-template-${idx}`}
                >
                  <div className="template-schedule">
                    <span className="days-badge">
                      {template.days_after_signup === 0 ? 'Immediately' :
                        template.days_after_signup === 1 ? 'Day 1' :
                        `Day ${template.days_after_signup}`}
                    </span>
                  </div>
                  <div className="template-info">
                    <div className="template-name">{template.name}</div>
                    <div className="template-subject">{template.subject}</div>
                    <div className="template-meta">
                      <span className={`status-indicator ${template.enabled ? 'active' : 'inactive'}`}>
                        {template.enabled ? 'Active' : 'Inactive'}
                      </span>
                      <span className="sent-count">{template.sent_count || 0} sent</span>
                    </div>
                  </div>
                  <div className="template-actions">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleOnboardingTemplate(template.id)}
                      disabled={togglingTemplate === template.id}
                      title={template.enabled ? 'Disable' : 'Enable'}
                    >
                      {togglingTemplate === template.id ? (
                        <Loader2 size={16} className="spinning" />
                      ) : (
                        <Power size={16} className={template.enabled ? 'text-green' : 'text-gray'} />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openOnboardingModal(template)}
                      title="Edit"
                    >
                      <Edit size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => testOnboardingTemplate(template.id)}
                      title="Send test to yourself"
                    >
                      <Send size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteOnboardingTemplate(template.id)}
                      className="delete-btn"
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="onboarding-info">
            <h4>How Onboarding Emails Work</h4>
            <ul>
              <li><strong>Day 0 (Immediately):</strong> Sent right after signup</li>
              <li><strong>Day 1, 3, 7, etc.:</strong> Sent on that day after signup</li>
              <li>Emails run daily at 7:00 AM UTC</li>
              <li>Test users and opted-out users are automatically excluded</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {showOnboardingModal && (
        <div className="modal-overlay" onClick={() => setShowOnboardingModal(false)}>
          <div className="modal-content onboarding-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingTemplate ? 'Edit Template' : 'Create Onboarding Template'}</h3>
              <button className="close-btn" onClick={() => setShowOnboardingModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="onboarding-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Template Name *</label>
                    <Input
                      value={onboardingForm.name}
                      onChange={(e) => setOnboardingForm({ ...onboardingForm, name: e.target.value })}
                      placeholder="e.g., Welcome Email, Tips & Tricks"
                    />
                  </div>
                  <div className="form-group days-group">
                    <label>Days After Signup *</label>
                    <Input
                      type="number"
                      min="0"
                      max="365"
                      value={onboardingForm.days_after_signup}
                      onChange={(e) => setOnboardingForm({ ...onboardingForm, days_after_signup: parseInt(e.target.value) || 0 })}
                    />
                    <span className="days-hint">{onboardingForm.days_after_signup === 0 ? 'Immediately' : `${onboardingForm.days_after_signup} day${onboardingForm.days_after_signup > 1 ? 's' : ''} after signup`}</span>
                  </div>
                </div>

                <div className="form-group">
                  <label>Email Subject *</label>
                  <Input
                    value={onboardingForm.subject}
                    onChange={(e) => setOnboardingForm({ ...onboardingForm, subject: e.target.value })}
                    placeholder="Welcome to Baad.tv!"
                  />
                </div>

                <div className="form-group">
                  <label>Heading Title *</label>
                  <Input
                    value={onboardingForm.title}
                    onChange={(e) => setOnboardingForm({ ...onboardingForm, title: e.target.value })}
                    placeholder="Welcome to the Flock!"
                  />
                </div>

                <div className="form-group">
                  <label>Content *</label>
                  <p className="field-hint" style={{ color: '#999', fontSize: '0.8rem', margin: '0.25rem 0 0.5rem' }}>
                    Add text and image blocks. Drag to reorder.
                  </p>

                  <div className="content-blocks">
                    {onboardingBlocks.map((block, index) => (
                      <div
                        key={block.id}
                        className={`content-block ${block.type} ${onboardingDraggedIndex === index ? 'dragging' : ''}`}
                        draggable
                        onDragStart={(e) => handleOnboardingDragStart(e, index)}
                        onDragOver={(e) => handleOnboardingDragOver(e, index)}
                        onDragEnd={handleOnboardingDragEnd}
                        data-testid={`onboarding-block-${index}`}
                      >
                        <div className="block-header">
                          <div className="block-drag-handle"><GripVertical size={16} /></div>
                          <div className="block-type-label">
                            {block.type === 'text' && <><Type size={14} /> Text Block</>}
                            {block.type === 'image' && <><Image size={14} /> Image</>}
                          </div>
                          <div className="block-controls">
                            <button
                              className="block-move-btn"
                              onClick={() => moveOnboardingBlock(index, -1)}
                              disabled={index === 0}
                              title="Move up"
                            >
                              <ChevronUp size={16} />
                            </button>
                            <button
                              className="block-move-btn"
                              onClick={() => moveOnboardingBlock(index, 1)}
                              disabled={index === onboardingBlocks.length - 1}
                              title="Move down"
                            >
                              <ChevronDown size={16} />
                            </button>
                            <button
                              className="block-remove-btn"
                              onClick={() => removeOnboardingBlock(block.id)}
                              title="Remove block"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>

                        <div className="block-content">
                          {block.type === 'text' && (
                            <textarea
                              value={block.content}
                              onChange={(e) => updateOnboardingBlock(block.id, e.target.value)}
                              placeholder={"Enter your text here...\n\nUse line breaks to format your message."}
                              rows={4}
                              className="block-textarea"
                              data-testid={`onboarding-block-text-${index}`}
                            />
                          )}
                          {block.type === 'image' && (
                            <div className="image-block-input">
                              <div className="image-upload-options">
                                <label className="upload-btn" data-testid={`onboarding-upload-image-${index}`}>
                                  {onboardingUploadingBlock === block.id ? (
                                    <Loader2 size={16} className="spinning" />
                                  ) : (
                                    <Plus size={16} />
                                  )}
                                  {onboardingUploadingBlock === block.id ? 'Uploading...' : 'Upload Image'}
                                  <input
                                    type="file"
                                    accept="image/jpeg,image/png,image/gif,image/webp"
                                    onChange={(e) => handleOnboardingImageUpload(block.id, e.target.files[0])}
                                    disabled={onboardingUploadingBlock === block.id}
                                    style={{ display: 'none' }}
                                  />
                                </label>
                                <span className="or-divider">or</span>
                                <Input
                                  value={block.content}
                                  onChange={(e) => updateOnboardingBlock(block.id, e.target.value)}
                                  placeholder="Paste image URL"
                                  className="url-input"
                                  data-testid={`onboarding-block-image-url-${index}`}
                                />
                              </div>
                              {block.content && (
                                <div className="image-preview">
                                  <img
                                    src={block.content}
                                    alt="Preview"
                                    onError={(e) => e.target.style.display = 'none'}
                                  />
                                  <button
                                    className="remove-image-btn"
                                    onClick={() => updateOnboardingBlock(block.id, '')}
                                    title="Remove image"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="add-block-buttons">
                    <Button
                      variant="outline"
                      onClick={() => addOnboardingBlock('text')}
                      className="add-block-btn"
                      data-testid="add-onboarding-text-block"
                    >
                      <Plus size={16} /> <Type size={16} /> Add Text
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => addOnboardingBlock('image')}
                      className="add-block-btn"
                      data-testid="add-onboarding-image-block"
                    >
                      <Plus size={16} /> <Image size={16} /> Add Image
                    </Button>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Button Text</label>
                    <Input
                      value={onboardingForm.cta_text}
                      onChange={(e) => setOnboardingForm({ ...onboardingForm, cta_text: e.target.value })}
                      placeholder="Get Started"
                    />
                  </div>
                  <div className="form-group">
                    <label>Button URL</label>
                    <Input
                      value={onboardingForm.cta_url}
                      onChange={(e) => setOnboardingForm({ ...onboardingForm, cta_url: e.target.value })}
                      placeholder="https://baad.tv"
                    />
                  </div>
                </div>

                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={onboardingForm.enabled}
                      onChange={(e) => setOnboardingForm({ ...onboardingForm, enabled: e.target.checked })}
                    />
                    <span>Enabled (active for new signups)</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <Button variant="outline" onClick={() => setShowOnboardingModal(false)}>
                Cancel
              </Button>
              <Button onClick={saveOnboardingTemplate} disabled={savingOnboarding}>
                {savingOnboarding ? (
                  <><Loader2 size={16} className="spinning" /> Saving...</>
                ) : (
                  <><Save size={16} /> {editingTemplate ? 'Update' : 'Create'} Template</>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnboardingTab;
